/**
 * Real client-side auth material — a Telegram Mini App session, the native
 * iOS runtime (session headers attach server-side), or a browser web session.
 *
 * Background pollers must check this before hitting signed-in endpoints:
 * demo / guest visitors otherwise spray 401s across the console (crews,
 * company-dashboard access, WM badges…). A stale legacy user-id in
 * localStorage is NOT auth material — it still 401s.
 */
import { hostWebApp } from './hostWebApp';
import { getWebSessionToken, hasActiveWebSession } from './webSessionAuth';
import { isIosNativeAppRuntime } from './runtimeCapabilities';
import { activeSavedAccount, isExplicitlySignedOut } from './accountSessions';
import { getNativeIosLinkedUser } from './nativeApiRouting';

export function hasAppAuthMaterial(): boolean {
  try {
    if (isIosNativeAppRuntime()) return true;
    if (hostWebApp.getInitData?.()) return true;
  } catch { /* host bridge unavailable */ }
  return hasActiveWebSession();
}

/**
 * Credential-backed signed-in account for chrome (Upgrade/Post, App auth gate).
 * Web session, saved active account, native linked session, or host bridge user.
 * Leftover localStorage user ids alone do not count.
 */
export function isClientSignedIn(): boolean {
  if (isExplicitlySignedOut()) return false;
  if (hasActiveWebSession()) return true;
  if (activeSavedAccount()) return true;
  if (getNativeIosLinkedUser()?.sessionToken) return true;
  try {
    if (hostWebApp.getUser()?.id) return true;
  } catch { /* host bridge missing in tests */ }
  return false;
}

/** Opaque, memory-only comparison key. Never log or persist credentials from this value. */
export function getClientSessionKey(): string {
  if (isExplicitlySignedOut()) return 'signed-out';
  const webToken = getWebSessionToken();
  if (webToken) return `web:${webToken}`;
  const native = getNativeIosLinkedUser();
  if (native?.sessionToken) return `native:${native.userId}:${native.sessionToken}`;
  try {
    const initData = hostWebApp.getInitData?.();
    if (initData) return `host:${initData}`;
    const user = hostWebApp.getUser?.();
    if (user?.id) return `host-user:${user.id}`;
  } catch { /* unavailable bridge */ }
  return 'signed-out';
}
